import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import styles from "../../styles/report/reporthead.module.scss";
import { useSelector, useDispatch } from "react-redux";
import {
  getClientProfile,
  getClientReport,
} from "../../services/clientService";
import { useNavigate, useLocation } from "react-router-dom";
import { ApiEndpoints, LocalStorageKeys } from "../../core";

const ClientReportHead = ({ report }) => {
  const { profile } = useSelector((state) => state.client);
  const dispatch = useDispatch();
  const [isDisabled, setIsDisabled] = useState(false);
  const [selectedYear, setSelectedYear] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const hasReports = Array.isArray(report) && report.length > 0; // ✅

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    getClientProfile();
    // Load initial data with current year
    const currentYear = new Date().getFullYear();
    setSelectedYear(currentYear.toString());
    handleYearFilter({ target: { value: currentYear.toString() } });
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return "";

    const date = new Date(dateString);

    // Check if date is valid
    if (isNaN(date.getTime())) {
      return "";
    }

    return date.toLocaleDateString("en-GB", {
      timeZone: "Asia/Dubai",
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const formatTime = (dateString) => {
    if (!dateString) return "";

    const date = new Date(dateString);

    if (isNaN(date.getTime())) {
      return "";
    }

    return date.toLocaleTimeString("en-GB", {
      timeZone: "Asia/Dubai",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const clientData = {
    lastTestDate: formatDate(profile?.lastReportTestedAt || ""),
    lastTestTime: formatTime(profile?.lastReportTestedAt || ""),
  };

  const handleYearFilter = async (e) => {
    const year = e.target.value;
    setSelectedYear(year);

    try {
      // Show loading state
      setIsDisabled(true);

      const success = await getClientReport(year);
    } catch (error) {
      console.error("Year filter error:", error);
      toast.error("Failed to filter reports by year");
    } finally {
      setIsDisabled(false);
    }
  };

  const handleDownloadPDF = async () => {
    if (!hasReports) {
      toast.error("No reports found to download for the selected year.");
      return;
    }
    const clientId = profile?._id;
    const clientName = profile?.name;
    if (!clientId) {
      console.error("Client ID not found.");
      toast.error("Client ID not found.");
      return;
    }
    const token = localStorage.getItem(LocalStorageKeys.clientToken);

    const downloadUrl = `${ApiEndpoints.baseApiURL}/fitness-report/download/client`;
    toast.success("Preparing download...");
    setIsDisabled(true);
    try {
      const response = await fetch(downloadUrl, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.status === 204) {
        toast.error("No reports found to download.");
        return;
      }
      if (!response.ok) {
        throw new Error("Failed to fetch PDF");
      }
      const blob = await response.blob();
      if (!blob.size) {
        toast.error("No reports found to download.");
        return;
      }
      const blobUrl = window.URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `${clientName}-${selectedYear}-report.pdf`; // ✅ File name for download
      document.body.appendChild(a);
      a.click();

      a.remove();
      window.URL.revokeObjectURL(blobUrl);
      toast.success("Download completed!");
    } catch (error) {
      console.error("PDF download failed:", error);
      toast.error("Failed to download PDF");
    } finally {
      setIsDisabled(false);
    }
  };

  const handleBack = () => {
    navigate("/");
  };

  // Generate year options
  const currentYear = new Date().getFullYear();
  const startYear = 2025;
  const yearOptions = [];

  for (let year = currentYear; year >= startYear; year--) {
    yearOptions.push({
      value: year.toString(),
      label: year === currentYear ? `${year}` : year.toString(),
    });
  }
  return (
    <div className={styles.container}>
      {/* Header Section */}
      <div className={styles.buttonGroup}>
        <div className={styles.buttonGroupchildone}>
          <h1 className={styles.title}>CLIENT REPORT</h1>
        </div>
        <div className={styles.buttonGroupchildtwo}>
          <select
            className={styles.sortselect}
            value={selectedYear}
            onChange={handleYearFilter}
            disabled={isDisabled}
          >
            <option disabled value="all">
              Select Years
            </option>
            {yearOptions.map((yearOption) => (
              <option
                defaultChecked
                key={yearOption.value}
                value={yearOption.value}
              >
                {yearOption.label}
              </option>
            ))}
          </select>

          <button
            disabled={isDisabled}
            onClick={handleDownloadPDF}
            className={styles.downloadButton}
          >
            <span className={styles.buttonText}>
              {isDisabled ? "Processing..." : "Download PDF"}
            </span>{" "}
          </button>

          <button
            onClick={handleBack}
            className={styles.backButton}
            style={{
              display: location.pathname === "/scoreboard" ? "" : "none",
            }}
          >
            Back
          </button>
        </div>
      </div>

      {/* Client Information Section */}
      <div className={styles.clientInfo}>
        <div className={styles.infoGrid}>
          <div className={styles.infoItem}>
            <span className={styles.label}>Client Name:</span>
            <span className={styles.value}>{profile?.name || ""}</span>
          </div>

          <div className={styles.infoItem}>
            <span className={styles.label}>Client ID:</span>
            <span className={styles.value}>{profile?.code || ""}</span>
          </div>

          <div className={styles.infoItem}>
            <span className={styles.label}>Gender:</span>
            <span className={styles.value}>{profile?.gender || ""}</span>
          </div>

          <div className={styles.infoItem}>
            <span className={styles.label}>DOB:</span>
            <span className={styles.value}>{profile?.dob || ""}</span>
          </div>

          <div className={styles.infoItem}>
            <span className={styles.label}>Last Test Date :</span>
            <span className={styles.value}>
              {clientData.lastTestDate || "--"}{" "}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientReportHead;

//PDF

// import React, { useEffect } from "react";
// import styles from "../../styles/report/reporthead.module.scss";
// import { useSelector } from "react-redux";
// import { getClientProfile } from "../../services/clientService";
// import { useNavigate, useLocation } from "react-router-dom";

// const ClientReportHead = () => {
//   const { profile } = useSelector((state) => state.client);

//   // You'll need to get this data from your Redux store - replace with actual selectors
//   const {
//     exerciseData,
//     biometricData,
//     notesData,
//     categoryData,
//     topPerformanceData
//   } = useSelector((state) => ({
//     exerciseData: state.client.exerciseData || [],
//     biometricData: state.client.biometricData || [],
//     notesData: state.client.notesData || [],
//     categoryData: state.client.categoryData || [],
//     topPerformanceData: state.client.topPerformanceData || {}
//   }));

//   useEffect(() => {
//     getClientProfile();
//   }, []);

//   const navigate = useNavigate();
//   const location = useLocation();

//   const formatDate = (dateString) => {
//     if (!dateString) return "";
//     const date = new Date(dateString);
//     if (isNaN(date.getTime())) return "";
//     return date.toLocaleDateString("en-GB", {
//       timeZone: "Asia/Dubai",
//       day: "2-digit",
//       month: "2-digit",
//       year: "numeric",
//     });
//   };

//   const formatTime = (dateString) => {
//     if (!dateString) return "";
//     const date = new Date(dateString);
//     if (isNaN(date.getTime())) return "";
//     return date.toLocaleTimeString("en-GB", {
//       timeZone: "Asia/Dubai",
//       hour: "2-digit",
//       minute: "2-digit",
//       hour12: true,
//     });
//   };

//   const clientData = {
//     lastTestDate: formatDate(profile?.lastReportTestedAt || ""),
//     lastTestTime: formatTime(profile?.lastReportTestedAt || ""),
//   };

//   // Sample data structure - replace with your actual Redux data
//   const reportData = {
//     exercises: exerciseData.length > 0 ? exerciseData : [
//       {
//         block: 1,
//         date: "01/10/2024",
//         rowerErg: { client: "2208", group: "2019" },
//         backSquat: { client: "2208", group: "2019" },
//         trapBar: { client: "2208", group: "2019" },
//         benchPress: { client: "2208", group: "2019" },
//         bentOverRow: { client: "2208", group: "2019" }
//       },
//       {
//         block: 2,
//         date: "04/01/2025",
//         rowerErg: { client: "2317", group: "2149" },
//         backSquat: { client: "2317", group: "2149" },
//         trapBar: { client: "2317", group: "2149" },
//         benchPress: { client: "2317", group: "2149" },
//         bentOverRow: { client: "2317", group: "2149" }
//       },
//       {
//         block: 3,
//         date: "12/03/2025",
//         rowerErg: { client: "0", group: "0" },
//         backSquat: { client: "0", group: "0" },
//         trapBar: { client: "0", group: "0" },
//         benchPress: { client: "0", group: "0" },
//         bentOverRow: { client: "0", group: "0" }
//       },
//       {
//         block: 4,
//         date: "08/06/2025",
//         rowerErg: { client: "0", group: "0" },
//         backSquat: { client: "0", group: "0" },
//         trapBar: { client: "0", group: "0" },
//         benchPress: { client: "0", group: "0" },
//         bentOverRow: { client: "0", group: "0" }
//       }
//     ],
//     biometrics: biometricData.length > 0 ? biometricData : [
//       { block: 1, bodyWeight: 62, muscleMass: 27, bodyFat: 19 },
//       { block: 2, bodyWeight: 63, muscleMass: 27, bodyFat: 20 },
//       { block: 3, bodyWeight: 0, muscleMass: 0, bodyFat: 0 },
//       { block: 4, bodyWeight: 0, muscleMass: 0, bodyFat: 0 }
//     ],
//     notes: notesData.length > 0 ? notesData : [
//       { quarter: 1, note: "Hello well done great stuff" },
//       { quarter: 2, note: "be better" },
//       { quarter: 3, note: "be stronger" },
//       { quarter: 4, note: "be fitter" }
//     ],
//     categories: categoryData.length > 0 ? categoryData : [
//       {
//         name: "Category 1",
//         participants: ["Ben", "Joe", "Jack", "Ron", "Max", "Sam", "Luke", "Tom", "Nate", "Kyle"],
//         values: [580, 520, 450, 400, 350, 300, 500, 380, 420, 460]
//       },
//       {
//         name: "Category 2",
//         participants: ["Ben", "Joe", "Jack", "Ron", "Max", "Sam", "Luke", "Tom", "Nate", "Kyle"],
//         values: [600, 540, 470, 420, 370, 320, 520, 400, 440, 480]
//       },
//       {
//         name: "Category 3",
//         participants: ["Ben", "Joe", "Jack", "Ron", "Max", "Sam", "Luke", "Tom", "Nate", "Kyle"],
//         values: [620, 560, 490, 440, 390, 340, 540, 420, 460, 500]
//       },
//       {
//         name: "Category 4",
//         participants: ["Ben", "Joe", "Jack", "Ron", "Max", "Sam", "Luke", "Tom", "Nate", "Kyle"],
//         values: [590, 530, 460, 410, 360, 310, 510, 390, 430, 470]
//       },
//       {
//         name: "Category 5",
//         participants: ["Ben", "Joe", "Jack", "Ron", "Max", "Sam", "Luke", "Tom", "Nate", "Kyle"],
//         values: [610, 550, 480, 430, 380, 330, 530, 410, 450, 490]
//       },
//       {
//         name: "Category 6",
//         participants: ["Ben", "Joe", "Jack", "Ron", "Max", "Sam", "Luke", "Tom", "Nate", "Kyle"],
//         values: [570, 510, 440, 390, 340, 290, 490, 370, 410, 450]
//       }
//     ],
//     topPerformances: {
//       male: topPerformanceData.male || [
//         { name: "Ben", score: 450 },
//         { name: "Joe", score: 420 },
//         { name: "Jack", score: 380 },
//         { name: "Ron", score: 350 }
//       ],
//       female: topPerformanceData.female || [
//         { name: "Olivia", score: 430 },
//         { name: "Ava", score: 400 },
//         { name: "Sophia", score: 370 },
//         { name: "Isabella", score: 340 }
//       ]
//     }
//   };

//   const handleDownloadPDF = () => {
//     const printWindow = window.open('', '_blank');

//     const printContent = `
//       <!DOCTYPE html>
//       <html lang="en">
//       <head>
//           <meta charset="UTF-8">
//           <meta name="viewport" content="width=device-width, initial-scale=1.0">
//           <title>Client Report - ${profile?.name || 'Unknown'}</title>
//           <style>
//               @page {
//                   margin: 0.5in;
//                   size: A4;
//               }
//               @media print {
//                   body { -webkit-print-color-adjust: exact; }
//                   .no-print { display: none !important; }
//               }
//               body {
//                   font-family: 'Arial', sans-serif;
//                   background-color: black !important;
//                   color: white !important;
//                   padding: 20px;
//                   margin: 0;
//                   -webkit-print-color-adjust: exact;
//                   print-color-adjust: exact;
//               }
//               .container {
//                   max-width: 100%;
//                   margin: 0 auto;
//               }
//               .header {
//                   margin-bottom: 24px;
//               }
//               .title {
//                   color: #F97316 !important;
//                   font-size: 16px;
//                   font-weight: bold;
//                   margin-bottom: 16px;
//               }
//               .client-info {
//                   display: grid;
//                   grid-template-columns: repeat(5, 1fr);
//                   gap: 16px;
//                   font-size: 12px;
//                   margin-bottom: 32px;
//               }
//               .info-item {
//                   display: flex;
//                   flex-direction: column;
//               }
//               .label {
//                   color: #9CA3AF !important;
//                   margin-bottom: 4px;
//                   font-weight: normal;
//               }
//               .value {
//                   color: white !important;
//                   font-weight: 500;
//               }
//               .section-title {
//                   color: white !important;
//                   font-size: 14px;
//                   font-weight: bold;
//                   margin: 32px 0 16px 0;
//               }
//               .table-container {
//                   background-color: #111827 !important;
//                   border-radius: 8px;
//                   overflow: hidden;
//                   margin-bottom: 32px;
//                   border: 1px solid #374151;
//               }
//               table {
//                   width: 100%;
//                   border-collapse: collapse;
//                   font-size: 11px;
//               }
//               th {
//                   background-color: #1F2937 !important;
//                   color: white !important;
//                   padding: 8px;
//                   text-align: left;
//                   font-weight: bold;
//                   border-bottom: 1px solid #374151;
//               }
//               td {
//                   padding: 8px;
//                   border-bottom: 1px solid #374151;
//                   color: white !important;
//               }
//               .center { text-align: center; }
//               .group-avg {
//                   color: #9CA3AF !important;
//                   font-size: 10px;
//                   margin-top: 2px;
//               }
//               .chart-section {
//                   display: grid;
//                   grid-template-columns: repeat(3, 1fr);
//                   gap: 16px;
//                   margin: 24px 0;
//               }
//               .chart-placeholder {
//                   background-color: #111827 !important;
//                   border: 1px solid #374151;
//                   border-radius: 8px;
//                   padding: 16px;
//                   height: 200px;
//                   display: flex;
//                   flex-direction: column;
//                   justify-content: center;
//                   align-items: center;
//                   color: #6B7280 !important;
//                   font-size: 12px;
//               }
//               .category-grid {
//                   display: grid;
//                   grid-template-columns: repeat(2, 1fr);
//                   gap: 16px;
//                   margin: 24px 0;
//               }
//               .performance-grid {
//                   display: grid;
//                   grid-template-columns: repeat(2, 1fr);
//                   gap: 16px;
//                   margin: 24px 0;
//               }
//               .print-button {
//                   background-color: #F97316;
//                   color: white;
//                   border: none;
//                   padding: 10px 20px;
//                   border-radius: 4px;
//                   cursor: pointer;
//                   margin: 20px 0;
//                   font-size: 14px;
//               }
//               .print-button:hover {
//                   background-color: #EA580C;
//               }
//               .radar-placeholder {
//                   background-color: #111827 !important;
//                   border: 1px solid #374151;
//                   border-radius: 8px;
//                   padding: 16px;
//                   height: 200px;
//                   display: flex;
//                   flex-direction: column;
//                   justify-content: center;
//                   align-items: center;
//                   color: #6B7280 !important;
//                   font-size: 12px;
//               }
//               .legend {
//                   display: flex;
//                   gap: 16px;
//                   margin-bottom: 16px;
//                   font-size: 10px;
//               }
//               .legend-item {
//                   display: flex;
//                   align-items: center;
//                   gap: 4px;
//               }
//               .legend-color {
//                   width: 12px;
//                   height: 12px;
//                   border-radius: 2px;
//               }
//           </style>
//       </head>
//       <body>
//           <div class="container">
//               <button onclick="window.print()" class="print-button no-print">Print / Save as PDF</button>

//               <!-- Header -->
//               <div class="header">
//                   <h1 class="title">CLIENT REPORT</h1>
//                   <div class="client-info">
//                       <div class="info-item">
//                           <span class="label">Client Name:</span>
//                           <span class="value">${profile?.name || 'N/A'}</span>
//                       </div>
//                       <div class="info-item">
//                           <span class="label">Client ID:</span>
//                           <span class="value">${profile?.code || 'N/A'}</span>
//                       </div>
//                       <div class="info-item">
//                           <span class="label">Gender:</span>
//                           <span class="value">${profile?.gender || 'N/A'}</span>
//                       </div>
//                       <div class="info-item">
//                           <span class="label">DOB:</span>
//                           <span class="value">${profile?.dob || 'N/A'}</span>
//                       </div>
//                       <div class="info-item">
//                           <span class="label">Last Test Date / Time:</span>
//                           <span class="value">${clientData.lastTestDate} ${clientData.lastTestTime}</span>
//                       </div>
//                   </div>
//               </div>

//               <!-- Exercises Report -->
//               <div>
//                   <h2 class="section-title">Exercises Report</h2>
//                   <div class="table-container">
//                       <table>
//                           <thead>
//                               <tr>
//                                   <th>Block Date</th>
//                                   <th class="center">Rower ERG</th>
//                                   <th class="center">Back SQUAT</th>
//                                   <th class="center">trap bar</th>
//                                   <th class="center">bench Press</th>
//                                   <th class="center">Bent over Row</th>
//                               </tr>
//                           </thead>
//                           <tbody>
//                               ${reportData.exercises.map(exercise => `
//                                   <tr>
//                                       <td>
//                                           <div>${exercise.block} ${exercise.date}</div>
//                                           <div class="group-avg">Group Average</div>
//                                       </td>
//                                       <td class="center">
//                                           <div>${exercise.rowerErg.client}</div>
//                                           <div class="group-avg">${exercise.rowerErg.group}</div>
//                                       </td>
//                                       <td class="center">
//                                           <div>${exercise.backSquat.client}</div>
//                                           <div class="group-avg">${exercise.backSquat.group}</div>
//                                       </td>
//                                       <td class="center">
//                                           <div>${exercise.trapBar.client}</div>
//                                           <div class="group-avg">${exercise.trapBar.group}</div>
//                                       </td>
//                                       <td class="center">
//                                           <div>${exercise.benchPress.client}</div>
//                                           <div class="group-avg">${exercise.benchPress.group}</div>
//                                       </td>
//                                       <td class="center">
//                                           <div>${exercise.bentOverRow.client}</div>
//                                           <div class="group-avg">${exercise.bentOverRow.group}</div>
//                                       </td>
//                                   </tr>
//                               `).join('')}
//                           </tbody>
//                       </table>
//                   </div>
//               </div>

//               <!-- Two Column Layout: Biometric Report and Category Name -->
//               <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 24px; margin-bottom: 32px;">
//                   <!-- Biometric Report -->
//                   <div>
//                       <h2 class="section-title">Biometric Report</h2>
//                       <div class="table-container">
//                           <table>
//                               <thead>
//                                   <tr>
//                                       <th>Block</th>
//                                       <th class="center">Body Weight (kg)</th>
//                                       <th class="center">Muscle Mass (kg)</th>
//                                       <th class="center">Body Fat (%)</th>
//                                   </tr>
//                               </thead>
//                               <tbody>
//                                   ${reportData.biometrics.map(bio => `
//                                       <tr>
//                                           <td>${bio.block}</td>
//                                           <td class="center">${bio.bodyWeight}</td>
//                                           <td class="center">${bio.muscleMass}</td>
//                                           <td class="center">${bio.bodyFat}</td>
//                                       </tr>
//                                   `).join('')}
//                               </tbody>
//                           </table>
//                       </div>
//                   </div>

//                   <!-- Category Name Radar Chart -->
//                   <div>
//                       <h2 class="section-title">Category Name</h2>
//                       <div class="radar-placeholder">
//                           <div class="legend">
//                               <div class="legend-item">
//                                   <div class="legend-color" style="background-color: #6B7280;"></div>
//                                   <span>BLOCK 1</span>
//                               </div>
//                               <div class="legend-item">
//                                   <div class="legend-color" style="background-color: #9CA3AF;"></div>
//                                   <span>BLOCK 2</span>
//                               </div>
//                               <div class="legend-item">
//                                   <div class="legend-color" style="background-color: #F97316;"></div>
//                                   <span>BLOCK 3</span>
//                               </div>
//                               <div class="legend-item">
//                                   <div class="legend-color" style="background-color: #FCD34D;"></div>
//                                   <span>BLOCK 4</span>
//                               </div>
//                           </div>
//                           <div>Radar Chart Visualization</div>
//                           <div style="font-size: 10px; color: #9CA3AF;">Block data comparison</div>
//                       </div>
//                   </div>
//               </div>

//               <!-- Biometric Charts -->
//               <div class="chart-section">
//                   <div class="chart-placeholder">
//                       <h3 style="margin: 0 0 8px 0; font-size: 14px;">Body Weight (kg)</h3>
//                       <div>Block 1: ${reportData.biometrics[0]?.bodyWeight || 0}kg</div>
//                       <div>Block 2: ${reportData.biometrics[1]?.bodyWeight || 0}kg</div>
//                       <div>Block 3: ${reportData.biometrics[2]?.bodyWeight || 0}kg</div>
//                       <div>Block 4: ${reportData.biometrics[3]?.bodyWeight || 0}kg</div>
//                   </div>
//                   <div class="chart-placeholder">
//                       <h3 style="margin: 0 0 8px 0; font-size: 14px;">Muscle Mass (kg)</h3>
//                       <div>Block 1: ${reportData.biometrics[0]?.muscleMass || 0}kg</div>
//                       <div>Block 2: ${reportData.biometrics[1]?.muscleMass || 0}kg</div>
//                       <div>Block 3: ${reportData.biometrics[2]?.muscleMass || 0}kg</div>
//                       <div>Block 4: ${reportData.biometrics[3]?.muscleMass || 0}kg</div>
//                   </div>
//                   <div class="chart-placeholder">
//                       <h3 style="margin: 0 0 8px 0; font-size: 14px;">Body Fat (%)</h3>
//                       <div>Block 1: ${reportData.biometrics[0]?.bodyFat || 0}%</div>
//                       <div>Block 2: ${reportData.biometrics[1]?.bodyFat || 0}%</div>
//                       <div>Block 3: ${reportData.biometrics[2]?.bodyFat || 0}%</div>
//                       <div>Block 4: ${reportData.biometrics[3]?.bodyFat || 0}%</div>
//                   </div>
//               </div>

//               <!-- Notes Section -->
//               <div>
//                   <h2 class="section-title">Notes</h2>
//                   <div class="table-container">
//                       <table>
//                           <thead>
//                               <tr>
//                                   <th>Quarter</th>
//                                   <th>Notes</th>
//                               </tr>
//                           </thead>
//                           <tbody>
//                               ${reportData.notes.map(note => `
//                                   <tr>
//                                       <td>${note.quarter}</td>
//                                       <td>${note.note}</td>
//                                   </tr>
//                               `).join('')}
//                           </tbody>
//                       </table>
//                   </div>
//               </div>

//               <!-- Category Charts Grid -->
//               <div class="category-grid">
//                   ${reportData.categories.map(category => `
//                       <div class="chart-placeholder">
//                           <h3 style="margin: 0 0 8px 0; font-size: 14px;">${category.name}</h3>
//                           <div style="font-size: 10px;">Participants: ${category.participants.join(', ')}</div>
//                           <div style="margin-top: 8px; font-size: 10px;">
//                               Top Score: ${Math.max(...category.values)}
//                           </div>
//                       </div>
//                   `).join('')}
//               </div>

//               <!-- Top Performance Charts -->
//               <div class="performance-grid">
//                   <div class="chart-placeholder">
//                       <h3 style="margin: 0 0 8px 0; font-size: 14px;">Top 5 Overall Performances - Male</h3>
//                       ${reportData.topPerformances.male.map((performer, index) => `
//                           <div style="font-size: 11px; margin: 2px 0;">
//                               ${index + 1}. ${performer.name}: ${performer.score}
//                           </div>
//                       `).join('')}
//                   </div>
//                   <div class="chart-placeholder">
//                       <h3 style="margin: 0 0 8px 0; font-size: 14px;">Top 5 Overall Performances - Female</h3>
//                       ${reportData.topPerformances.female.map((performer, index) => `
//                           <div style="font-size: 11px; margin: 2px 0;">
//                               ${index + 1}. ${performer.name}: ${performer.score}
//                           </div>
//                       `).join('')}
//                   </div>
//               </div>
//           </div>
//       </body>
//       </html>
//     `;

//     printWindow.document.write(printContent);
//     printWindow.document.close();
//     printWindow.focus();

//     setTimeout(() => {
//       printWindow.print();
//     }, 500);
//   };

//   const handleBack = () => {
//     navigate("/");
//   };

//   return (
//     <div className={styles.container}>
//       {/* Header Section */}
//       <div className={styles.buttonGroup}>
//         <div className={styles.buttonGroupchildone}>
//           <h1 className={styles.title}>CLIENT REPORT</h1>
//         </div>
//         <div className={styles.buttonGroupchildtwo}>
//           <button onClick={handleDownloadPDF} className={styles.downloadButton}>
//             Download PDF
//           </button>

//           <button
//             onClick={handleBack}
//             className={styles.backButton}
//             style={{
//               display: location.pathname === "/scoreboard" ? "" : "none",
//             }}
//           >
//             Back
//           </button>
//         </div>
//       </div>

//       {/* Client Information Section */}
//       <div className={styles.clientInfo}>
//         <div className={styles.infoGrid}>
//           <div className={styles.infoItem}>
//             <span className={styles.label}>Client Name:</span>
//             <span className={styles.value}>{profile?.name || ""}</span>
//           </div>

//           <div className={styles.infoItem}>
//             <span className={styles.label}>Client ID:</span>
//             <span className={styles.value}>{profile?.code || ""}</span>
//           </div>

//           <div className={styles.infoItem}>
//             <span className={styles.label}>Gender:</span>
//             <span className={styles.value}>{profile?.gender || ""}</span>
//           </div>

//           <div className={styles.infoItem}>
//             <span className={styles.label}>DOB:</span>
//             <span className={styles.value}>{profile?.dob || ""}</span>
//           </div>

//           <div className={styles.infoItem}>
//             <span className={styles.label}>Last Test Date / Time:</span>
//             <span className={styles.value}>
//               {clientData.lastTestDate} {clientData.lastTestTime}
//             </span>
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default ClientReportHead;
